import { Zap, Gauge, MousePointerClick, ShieldCheck, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import FadeIn from "@/components/FadeIn";

const items = [
  {
    icon: Zap,
    title: "Speed",
    desc: "Live homepage preview delivered fast.",
    detail: "We deliver a working homepage preview within 3-5 business days. No lengthy discovery phases or drawn-out timelines. Local service businesses like plumbers, roofers, and law firms get to see their refreshed website before committing.",
  },
  {
    icon: Gauge,
    title: "Performance",
    desc: "Optimized for Core Web Vitals and Lighthouse.",
    detail: "Every website refresh targets Google Core Web Vitals: Largest Contentful Paint, Interaction to Next Paint, and Cumulative Layout Shift. Better Lighthouse scores mean faster load times, improved user experience, and stronger search engine rankings.",
  },
  {
    icon: MousePointerClick,
    title: "Conversion Flow",
    desc: "Clear CTAs and structured user paths.",
    detail: "We design user journeys with strategically placed call-to-action buttons, click-to-call links, and quote request forms. Every page guides visitors toward taking action, turning website traffic into real business leads.",
  },
  {
    icon: ShieldCheck,
    title: "SEO-Safe Structure",
    desc: "We preserve URLs and domain authority.",
    detail: "Your existing Google rankings and domain authority stay intact. We maintain your URL structure, set up 301 redirects where needed, and preserve meta tags and backlink equity so your local SEO performance only improves.",
  },
];

const ProofBar = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="section-padding section-alt" id="proof">
      <div className="container-narrow">
        <FadeIn>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="rounded-lg border border-border bg-background p-5 transition-shadow duration-300 hover:shadow-xl hover:shadow-accent/15 hover:border-accent/30"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10">
                    <item.icon className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">{item.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => toggle(i)}
                    className="flex w-full items-center justify-center gap-1 py-2 text-xs font-semibold text-accent hover:text-accent/80 transition-colors"
                    aria-expanded={openIndex === i}
                  >
                    Learn more
                    <motion.span
                      animate={{ rotate: openIndex === i ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <p className="text-xs leading-relaxed text-muted-foreground pb-1">
                          {item.detail}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </FadeIn>
        <FadeIn delay={0.15}>
          <p className="mt-8 text-center text-sm text-muted-foreground max-w-xl mx-auto">
            We keep what already works and fix what does not. Every change is tied to a specific outcome.
          </p>
        </FadeIn>
      </div>
    </section>
  );
};

export default ProofBar;